import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import { closeModal } from '../../actions/ModalActions';
import Modal from '../Modal';
import discountApi from './discountApi';
import { percent, benefitWindow, couponTerms } from '../../utils/discountFormat';

/**
 * Apply an active promotion or a bare coupon to a client, or redeem a
 * promotion code. A promotion brings its countries, eligibility and window;
 * a bare coupon is just the terms (the generic "20% off" sales can always
 * use). The API resolves the benefit window and answers with a stable
 * `reason` when the client is not eligible.
 */
@connect(
    () => ({}),
    dispatch => bindActionCreators({ closeModal }, dispatch)
)
export default class ApplyDiscountModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            mode: 'promotion',
            options: null,
            target: '',
            code: '',
            validation: null,
            anchorType: 'application_date',
            startsOn: moment().format('YYYY-MM-DD'),
            reason: '',
            isSaving: false,
            error: null
        };
    }

    componentDidMount() {
        // Active promotions (whose coupon is active) plus active coupons on
        // their own. Values are prefixed so one select can hold both.
        Promise.all([discountApi.listPromotions({ status: 'active' }), discountApi.listCoupons({ state: 'active' })])
            .then(([promotions, coupons]) => {
                var options = promotions
                    .filter(p => p.coupon && p.coupon.state === 'active')
                    .map(p => ({
                        value: `promotion:${p.promotion_id}`,
                        group: 'Promotions',
                        label: `${p.name} — ${p.coupon.invoice_name} (${couponTerms(p.coupon)}${p.code ? ', ' + p.code : ''})`
                    }))
                    .concat(coupons.map(c => ({
                        value: `coupon:${c.coupon_id}`,
                        group: 'Coupons (no promotion — no country or eligibility check)',
                        label: `${c.invoice_name} — ${couponTerms(c)}`
                    })));
                this.setState({ options });
            })
            .catch(error => this.setState({ options: [], error: error.message }));
    }

    targetBody() {
        var [kind, id] = this.state.target.split(':');
        return kind === 'coupon' ? { coupon_id: id } : { promotion_id: id };
    }

    anchorBody() {
        return {
            anchor_type: this.state.anchorType,
            starts_on: this.state.anchorType === 'explicit' ? this.state.startsOn : null
        };
    }

    checkCode() {
        this.setState({ isSaving: true, error: null, validation: null });
        discountApi.validateCode(this.props.client.id, { code: this.state.code, ...this.anchorBody() })
            .then(validation => this.setState({ isSaving: false, validation }))
            .catch(error => this.setState({ isSaving: false, error: error.message }));
    }

    handleSubmit() {
        var clientId = this.props.client.id;
        var request = this.state.mode === 'code'
            ? discountApi.redeemCode(clientId, { code: this.state.code, ...this.anchorBody() })
            : discountApi.applyDiscount(clientId, { ...this.targetBody(), reason: this.state.reason || null, ...this.anchorBody() });

        this.setState({ isSaving: true, error: null });
        request.then(() => {
            this.props.closeModal();
            if (this.props.onDone) {
                this.props.onDone();
            }
        }).catch(error => this.setState({ isSaving: false, error: error.message + (error.reason ? ` (${error.reason})` : '') }));
    }

    render() {
        var s = this.state;
        var canSubmit = !s.isSaving && (s.mode === 'code' ? s.code.trim() !== '' && (!s.validation || s.validation.valid) : s.target !== '');
        var actionButton = (
            <button className={`btn btn-success ${canSubmit ? '' : 'disabled'}`} onClick={canSubmit ? this.handleSubmit.bind(this) : undefined}>
                {s.isSaving ? 'Working...' : (s.mode === 'code' ? 'Redeem code' : 'Apply discount')}
            </button>
        );

        return (
            <Modal actionButton={actionButton} className="modal-primary" title={`Apply a discount to ${this.props.client.name}`}>
                {s.error && <div className="alert alert-danger">{s.error}</div>}
                <ul className="nav nav-tabs" style={{ marginBottom: 15 }}>
                    <li className={s.mode === 'promotion' ? 'active' : ''}><a href="#" onClick={e => { e.preventDefault(); this.setState({ mode: 'promotion', error: null }); }}>Promotion or coupon</a></li>
                    <li className={s.mode === 'code' ? 'active' : ''}><a href="#" onClick={e => { e.preventDefault(); this.setState({ mode: 'code', error: null }); }}>Promotion code</a></li>
                </ul>

                {s.mode === 'promotion' &&
                    <div className="form-group">
                        <label>Discount <small className="text-muted">active only</small></label>
                        {s.options === null && <p className="text-muted">Loading...</p>}
                        {s.options !== null &&
                            <select className="form-control" value={s.target} onChange={e => this.setState({ target: e.target.value })}>
                                <option value="">choose...</option>
                                {['Promotions', 'Coupons (no promotion — no country or eligibility check)'].map(group =>
                                    <optgroup key={group} label={group}>
                                        {s.options.filter(o => o.group === group).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </optgroup>
                                )}
                            </select>
                        }
                    </div>
                }
                {s.mode === 'code' &&
                    <div className="form-group">
                        <label>Promotion code</label>
                        <div className="input-group">
                            <input className="form-control" value={s.code} placeholder="SPRING26" onChange={e => this.setState({ code: e.target.value, validation: null })} />
                            <span className="input-group-btn">
                                <button className="btn btn-default" disabled={!s.code || s.isSaving} onClick={this.checkCode.bind(this)}>Check</button>
                            </span>
                        </div>
                        {s.validation && s.validation.valid &&
                            <p className="text-success" style={{ marginTop: 6 }}>
                                Valid: {s.validation.promotion.name} — {s.validation.coupon.invoice_name} ({percent(s.validation.coupon.percent_off)}) — {s.validation.state === 'pending_activation' ? 'pending until paid activation' : benefitWindow(s.validation.starts_on, s.validation.ends_on)}
                            </p>
                        }
                        {s.validation && !s.validation.valid &&
                            <p className="text-danger" style={{ marginTop: 6 }}>Not redeemable: {s.validation.reason}</p>
                        }
                    </div>
                }

                <div className="row">
                    <div className="col-xs-7">
                        <div className="form-group">
                            <label>Benefit starts</label>
                            <select className="form-control" value={s.anchorType} onChange={e => this.setState({ anchorType: e.target.value, validation: null })}>
                                <option value="application_date">today</option>
                                <option value="latest_activation">at latest paid activation (pending if none yet)</option>
                                <option value="explicit">on a specific date</option>
                            </select>
                        </div>
                    </div>
                    {s.anchorType === 'explicit' &&
                        <div className="col-xs-5">
                            <div className="form-group">
                                <label>Date</label>
                                <input type="date" className="form-control" value={s.startsOn} onChange={e => this.setState({ startsOn: e.target.value, validation: null })} />
                            </div>
                        </div>
                    }
                </div>
                {s.mode === 'promotion' &&
                    <div className="form-group">
                        <label>Reason <small className="text-muted">optional, kept in the audit trail</small></label>
                        <input className="form-control" value={s.reason} maxLength={500} onChange={e => this.setState({ reason: e.target.value })} />
                    </div>
                }
            </Modal>
        );
    }
}



// WEBPACK FOOTER //
// ./app/components/discounts/ApplyDiscountModal.js